import {useQuery} from "@apollo/client";
import capitalize from "lodash/capitalize";
import get from "lodash/get";
import {Suspense, useState} from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {isCustomPlan} from "@/lib/applogic/applogic.ts";
import {AccountUsageResponse, GQL_ACCOUNT_USAGE} from "@/lib/queries/checks.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {InstantCheckoutAvailable} from "@/pages/checks/components/form/InstantCheckout/InstantCheckoutAvailable.tsx";
import {InstantCheckoutFailure} from "@/pages/checks/components/form/InstantCheckout/InstantCheckoutFailure.tsx";
import {InstantCheckoutSuccess} from "@/pages/checks/components/form/InstantCheckout/InstantCheckoutSuccess.tsx";

enum InstantCheckoutState {
  INITIAL = "INITIAL",
  REQUESTED = "REQUESTED",
  FAILED = "FAILED",
}

interface InstantCheckoutProps {
  serviceType: MonitoringServiceType;
  accountCanPurchase: boolean;
  userCanPurchase: boolean;
  isNewCheck: boolean;
}

/*
 * list of services that have an updated checkout experience through a popup
 */
const EXCLUDED_SERVICES: MonitoringServiceType[] = [MonitoringServiceType.CLOUDSTATUS];

export const InstantCheckout = ({
  serviceType,
  accountCanPurchase,
  userCanPurchase,
  isNewCheck,
}: InstantCheckoutProps) => {
  const {user, brand} = useCurrentUserContext();
  // we don't have upper limits for custom plan, no instant checkout
  const customPlan = isCustomPlan(user!.account?.plan);
  const {data, loading, error} = useQuery<AccountUsageResponse>(GQL_ACCOUNT_USAGE, {
    skip: customPlan,
  });
  const [state, setState] = useState<InstantCheckoutState>(InstantCheckoutState.INITIAL);

  if (error) {
    console.error(error);
    return null;
  }
  if (customPlan || loading || !data || !isNewCheck) {
    return null;
  }
  if (EXCLUDED_SERVICES.includes(serviceType)) {
    return null;
  }

  const serviceUsage = data?.accountServicesUsage[serviceType];
  const serviceLimit = get(data?.plan.limits, serviceUsage?.limit_field);
  const hardLimit = serviceUsage?.used >= serviceLimit;
  return (
    <>
      {state === InstantCheckoutState.INITIAL && (
        <Suspense>
          <InstantCheckoutAvailable
            plan={capitalize(data?.plan.name)}
            planServiceLimit={serviceLimit}
            serviceUsage={serviceUsage}
            accountCanPurchase={accountCanPurchase}
            userCanPurchase={userCanPurchase}
            onRequest={() => {
              setState(InstantCheckoutState.REQUESTED);
            }}
            onRequestFailure={() => {
              setState(InstantCheckoutState.FAILED);
            }}
          />
        </Suspense>
      )}
      {state === InstantCheckoutState.REQUESTED && (
        <InstantCheckoutSuccess
          serviceUsage={serviceUsage}
          timezone={user!.account!.timezone}
          timeFormat={user!.current_time_format}
          dateFormat={user!.current_date_format}
          userCanPurchase={userCanPurchase && !hardLimit}
        />
      )}
      {state === InstantCheckoutState.FAILED && (
        <InstantCheckoutFailure
          brand={brand}
          serviceUsage={serviceUsage}
          userCanPurchase={userCanPurchase && !hardLimit}
        />
      )}
    </>
  );
};
